/* eslint-disable @typescript-eslint/no-var-requires */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  find,
  get,
  isEmpty,
  isEqual,
  isNil,
  isPlainObject,
  isString,
  reduce,
  some,
  sortBy,
  split,
  startsWith,
  toNumber,
  toString
} from 'lodash';
import { stringify, parse } from 'query-string';
import jsesc from 'jsesc';
import {
  ARIA_HIDDEN,
  EMPTY_STRING,
  SUPPORTED_LOCALES,
  COVERME_ORG,
  MANULIFE_EMPLOYEE_ORG,
  TRANSLATION_SPACE,
  ALLOWED_TARGET_LINKS,
  COUNTRIES,
  DAYJS_DIFF
} from '@trip-ux/constants';
import { dayjs } from '../dayjs';

export const TWO = 2;

export const invalidPlanError = (
  hasCFARPlan: boolean,
  isPPPPlan: boolean,
  quoteForm: { departureDate: string; travellers: { tripBookingDate: string }[] },
  dateFormat: string
): boolean => {
  const tripStartDate = dayjs(quoteForm.departureDate, dateFormat).startOf('day');
  const today = dayjs().startOf('day');
  const todayMinusThree = today.subtract(3, DAYJS_DIFF.DAYS);

  const hasInvalidTripBookingDate = quoteForm.travellers.some(traveller => {
    const tripBookingDateOnly = dayjs(traveller.tripBookingDate, dateFormat).startOf('day');
    return tripBookingDateOnly.isBefore(todayMinusThree) || tripBookingDateOnly.isAfter(today);
  });

  const isPPPNotValid = isPPPPlan && hasInvalidTripBookingDate;
  const hasInvalidCfarSelection = hasCFARPlan && !tripStartDate.isAfter(today.add(10, DAYJS_DIFF.DAYS));
  return hasInvalidCfarSelection || isPPPNotValid;
};

export interface ProductOptions {
  label: string;
  value: string;
}

// CSP-safe interpolation for ${key} syntax
export function interpolateTemplate(str: string | null | undefined, data: Record<string, any> = {}): string {
  // Defensive: if template is missing or not a string, return empty string
  if (typeof str !== 'string') {
    return '';
  }
  return str.replace(/\$\{(\w+)\}/g, (_, key) => {
    const val = data && data[key];
    // if null or undefined, return empty string; otherwise coerce to string
    return val === null || val === undefined ? '' : String(val);
  });
}

export const getAllCountries = (lang: string, suggestion?: string) => {
  let dropdownCountries = [];
  COUNTRIES[lang].forEach(val => {
    dropdownCountries.push({
      label: val,
      value: val
    });
  });
  if (!isEmpty(suggestion)) {
    dropdownCountries = dropdownCountries.filter(val => val.label.toLowerCase().includes(suggestion.toLowerCase()));
  }
  return sortBy(dropdownCountries, val => val.label);
};

export const getAllTravelLocations = travelLocations => {
  const dropdownTravelLocations = [];
  const offSet = 1;
  travelLocations.forEach((travelLocation, index) => {
    const iata = isEmpty(get(travelLocation, 'iata', '')) ? '' : `${travelLocation.iata}, `;
    const airportName = isEmpty(get(travelLocation, 'name', '')) ? '' : `${travelLocation.name}, `;
    const country = isEmpty(get(travelLocation, 'country', '')) ? '' : `${travelLocation.country}`;
    const combined = iata + airportName + country;
    if (!find(dropdownTravelLocations, { value: travelLocation.country })) {
      dropdownTravelLocations.push({ label: country, value: country, order: 0 });
    }
    dropdownTravelLocations.push({
      label: combined,
      value: combined,
      order: index + offSet
    });
  });

  return sortBy(dropdownTravelLocations, val => val.order);
};

export const travelLocationsSuggest = (agencyOptions, suggestion?: string) => {
  agencyOptions.sort((a, b) => {
    const labelA = a.label.toUpperCase();
    const labelB = b.label.toUpperCase();
    if (labelA < labelB) {
      return -1;
    }
    if (labelA > labelB) {
      return 1;
    }
    return 0;
  });
  agencyOptions.sort((a, b) => {
    if (a.order === 0) {
      return -1;
    }
    if (b.order === 0) {
      return 1;
    }
  });
  if (!isEmpty(suggestion)) {
    agencyOptions = agencyOptions.filter(val => val.label.toLowerCase().includes(suggestion.toLowerCase()));
  }
  return agencyOptions;
};

export const safeQueryString = (query: unknown): string =>
  stringify(JSON.parse(jsesc(isString(query) ? parse(query) : query, { json: true, isScriptContext: true })));

export const checkTrue = (str: unknown): boolean => toString(str) === 'true';

export const diffBetweenObjects = (obj1: object, obj2: object): Record<string, unknown> => {
  return reduce(
    obj1,
    function (result, value, key) {
      if (isPlainObject(value)) {
        result[key] = diffBetweenObjects(value, obj2[key]);
      } else if (!isEqual(value, obj2[key])) {
        result[key] = value;
      }
      return result;
    },
    {}
  );
};

export const converToNumber = (value: string | number | undefined): number =>
  toNumber(toString(value).replace(/,/g, '.'));

export const formatCurrency = (val: string | number, lang: string, decimalPlaces = TWO): string => {
  if (isNil(val)) {
    return EMPTY_STRING;
  }
  const value = isString(val) ? parseFloat(val).toFixed(decimalPlaces) : val.toFixed(decimalPlaces);
  const localeUsed = lang === SUPPORTED_LOCALES.FRENCH ? 'fr-ca' : 'en-us';
  return parseFloat(value).toLocaleString(localeUsed, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
};

export const formatCurrencyWithLabel = (val: string | number, lang: string, decimalPlaces = TWO): string => {
  if (isNil(val)) {
    return EMPTY_STRING;
  }
  let value = isString(val) ? parseFloat(val).toFixed(decimalPlaces) : val.toFixed(decimalPlaces);
  value =
    lang === SUPPORTED_LOCALES.FRENCH
      ? `${formatCurrency(value, lang, decimalPlaces)}$`
      : `$${formatCurrency(value, lang, decimalPlaces)}`;

  return value;
};

export const isWord = (value: string): boolean => {
  const r1 = /^[a-zA-Z]+$/;
  const letterRegExp = new RegExp(r1.source);
  return letterRegExp.test(value);
};

export const checkIfCoverMeOrManulifeEmployee = (orgCode: string): boolean => true;

export const getTranslationSpace = (orgCode: string): string => TRANSLATION_SPACE.CM;

export const showComponent = (flag, component) => {
  return flag && component;
};

export const setAriaHidden = (element: Element, state: string) => {
  element && element.setAttribute(ARIA_HIDDEN, state);
};

export const formatCurrencyObject = (amounts: ProductOptions[], lang: string, format: string) => {
  if (!isNil(amounts)) {
    for (const item of amounts) {
      if (!isWord(get(item, 'label'))) {
        item.label = interpolateTemplate(format, {
          amount: formatCurrency(get(item, 'value'), lang, 0)
        });
      }
    }
  }
  return amounts;
};

export const setRelAttribute = (href: string) => {
  const contextPath = split(window.location.pathname, '/')[1];

  if (some([`/${contextPath}`, ...ALLOWED_TARGET_LINKS], link => startsWith(href, link))) {
    return 'noopener';
  } else {
    return 'noopener noreferrer';
  }
};

export const checkIfManulifeEmployee = (orgCode: string): boolean => isEqual(orgCode, MANULIFE_EMPLOYEE_ORG);
export const checkIfCoverMe = (orgCode: string): boolean => isEqual(orgCode, COVERME_ORG);
export const getProvinceTranslation = (provinceOptions, province) => {
  const provinceTranslation = find(provinceOptions, { value: province });
  return get(provinceTranslation, 'label');
};

export const checkIfTransat = orgCode => orgCode !== COVERME_ORG && orgCode !== MANULIFE_EMPLOYEE_ORG;

export const getLocale = (userSelectedLocale: string): string => {
  return isEqual(userSelectedLocale, SUPPORTED_LOCALES.FRENCH) ? SUPPORTED_LOCALES.FRENCH : SUPPORTED_LOCALES.ENGLISH;
};

export const getManulifeID = (manulifeId: string, lang: string): string => {
  if (manulifeId) {
    const splitIndex = manulifeId.indexOf('ui_locales');
    const firstPart = manulifeId.substring(0, splitIndex);
    return `${firstPart}ui_locales=${lang.toLowerCase()}-CA`;
  } else {
    return '';
  }
};

export const getAEM = (url: string, lang: string): string => {
  if (url) {
    const index = url.indexOf('$');
    const firstPart = url.substring(0, index);
    const lastPart = url.substring(index + 7);
    return `${firstPart}${lang.toLowerCase()}${lastPart}`;
  } else {
    return '';
  }
};

/**
 * Resolves an error message from TMS labels using errorRef.
 * Falls back to the raw API errorMessage if no TMS entry exists.
 *
 * errorRef format: `PREFIX_DETAIL_PARTS` (e.g. `PAY_AMOUNT_MISMATCH`)
 * TMS lookup path: `errors.<prefix>.<detailsCamelCase>` (e.g. `errors.pay.amountMismatch`)
 */
export const resolveErrorMessage = (
  labels: Record<string, unknown>,
  errorRef?: string,
  fallbackMessage?: string
): string | undefined => {
  if (errorRef) {
    const [errorPrefix, ...detailParts] = errorRef.split('_');
    const errorDetails = detailParts
      .map((part, i) => (i === 0 ? part.toLowerCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()))
      .join('');
    const tmsMessage = get(labels, `errors.${errorPrefix.toLowerCase()}.${errorDetails}`) as string | undefined;
    if (tmsMessage) {
      return tmsMessage;
    }
  }
  return fallbackMessage;
};
